import React, { useState } from "react";
import { Editor } from "@tiptap/react";
import { Box, IconButton, Divider, Tooltip, ButtonGroup, Popover, Grid, Button, Paper } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBold,
    faItalic,
    faStrikethrough,
    faUnderline,
    faListUl,
    faListOl,
    faAlignLeft,
    faAlignCenter,
    faAlignRight,
    faAlignJustify,
    faQuoteRight,
    faCode,
    faBracketsCurly,
    faRotateLeft,
    faRotateRight,
    faTable,
    faTableRows,
    faTableColumns,
    faTrash,
    faPalette,
    faMinus,
    faMagnifyingGlassPlus,
    faMagnifyingGlassMinus,
    faArrowsRotate,
    faArrowUpFromBracket,
    faArrowDownToBracket,
    faArrowsUpDown,
    faFileWord,
    faFilePdf
} from "@fortawesome/pro-light-svg-icons";
import { exportToWord, exportToPDFViaWord } from "./utils/export";
import { JiraIcon } from "./components/JiraIcon";
import { AzureDevOpsIcon } from "./components/AzureDevOpsIcon";
import { RefreshIcon } from "@/assets/ui/icons/projectstudio";
import { useAuth } from "@clerk/clerk-react";
import { getUserStoryJiraExportData } from "@/api/userStoryApi";
import { getWorkProductionSectionById } from "@/api";
import { SyncPanel } from "@/components/sync/SyncPanel";
import { useAmbientSyncStatus, clearAmbientSyncCache } from "@/components/sync/useAmbientSyncStatus";
import { getSyncButtonState } from "./utils/syncButton";

interface TiptapToolbarProps {
    editor: Editor | null;
    // Jira export props
    projectId?: number;
    featureId?: string;
    userStoryId?: string;
    sectionId?: number;
    workProductTitle?: string;
    hasJiraLink?: boolean; // Whether the project has a Jira integration linked
    hasAzureDevOpsLink?: boolean; // Whether the project has an Azure DevOps integration linked
    // Rebuild props
    needsRebuild?: boolean;
    onRebuild?: () => void;
    workProductId?: number;
    // Export props
    allSections?: Array<{ id: string; title: string; markdown: string; sort_order?: number }>;
    // Navigate to the Integrations view — wired to SyncPanel's "Reconnect" CTA (see Task 16).
    onNavigateToIntegrations?: () => void;
}

interface ToolbarButtonProps {
    onClick: (e?: React.MouseEvent<HTMLButtonElement>) => void;
    active?: boolean;
    disabled?: boolean;
    icon: React.ReactNode;
    tooltip: string;
}

function ToolbarButton({ onClick, active, disabled, icon, tooltip }: ToolbarButtonProps) {
    return (
        <Tooltip title={tooltip} arrow placement="bottom">
            <span>
                <IconButton
                    onMouseDown={e => {
                        // Prevent button from stealing focus from editor
                        e.preventDefault();
                        onClick(e);
                    }}
                    disabled={disabled}
                    size="small"
                    sx={{
                        width: "24px",
                        height: "24px",
                        padding: "2px",
                        backgroundColor: active ? "rgba(0, 102, 204, 0.12)" : "transparent",
                        border: active ? "1px solid #0066cc" : "1px solid transparent",
                        borderRadius: "3px",
                        fontSize: "14px",
                        color: disabled ? "rgba(0, 0, 0, 0.26)" : "rgba(0, 0, 0, 0.87)",
                        "&:hover": {
                            backgroundColor: active ? "rgba(0, 102, 204, 0.18)" : "rgba(0, 0, 0, 0.08)"
                        }
                    }}
                >
                    {icon}
                </IconButton>
            </span>
        </Tooltip>
    );
}

/**
 * Toolbar for Tiptap editor with common formatting options
 * Designed to be compact and Word-like
 */
export default function TiptapToolbar({
    editor,
    projectId,
    featureId,
    userStoryId,
    sectionId,
    workProductTitle,
    hasJiraLink,
    hasAzureDevOpsLink,
    needsRebuild,
    onRebuild,
    workProductId,
    allSections,
    onNavigateToIntegrations
}: TiptapToolbarProps) {
    const [tableAnchor, setTableAnchor] = useState<HTMLButtonElement | null>(null);
    const [colorAnchor, setColorAnchor] = useState<HTMLButtonElement | null>(null);
    const [hoveredCell, setHoveredCell] = useState<{ row: number; col: number } | null>(null);
    const [zoomLevel, setZoomLevel] = useState(100);
    const [syncPanelOpen, setSyncPanelOpen] = useState(false);
    const [isExportingPdf, setIsExportingPdf] = useState(false);
    const getTimestamp = () => {
        const now = new Date();
        const pad = (n: number) => n.toString().padStart(2, "0");

        return now.getFullYear().toString().slice(2) + pad(now.getMonth() + 1) + pad(now.getDate()) + pad(now.getHours()) + pad(now.getMinutes());
    };
    const { getToken } = useAuth();

    const getUserStoryData = async (userStoryId: string | undefined, sectionId: number) => {
        let userStoryData: any = null;
        const token = await getToken();

        if (!token) {
            console.log("Unable to get authentication token");
            return;
        }

        try {
            if (userStoryId && sectionId) {
                console.log("Jira Export: Fetching user story data for userStoryId:", userStoryId);
                // Use the API function instead of direct fetch
                userStoryData = await getUserStoryJiraExportData(userStoryId, sectionId, token);
            } else if (sectionId) {
                console.log("Jira Export: Fetching Work Product Section for sectionId:", sectionId);
                userStoryData = await getWorkProductionSectionById(sectionId, token);
                console.log("Jira Export: User story data:", userStoryData);
            }
            console.log("Jira Export: User story data:", userStoryData);

            if (!userStoryData) {
                console.error("Jira Export: User story is missing");
                return;
            }
            return userStoryData;
        } catch (error: any) {
            console.error("Jira Export: Error fetching user story data:", error);
            return;
        }
    };

    // Sync button is shown for any open user story; the vendor flags only pick
    // the icon/tooltip copy, and SyncPanel resolves link/connection state after
    // the click (see syncButton.ts).
    const syncButton = getSyncButtonState({ userStoryId, hasJiraLink, hasAzureDevOpsLink });
    const vendorLabel = syncButton.vendor === "JIRA" ? "Jira" : syncButton.vendor === "AZURE_DEVOPS" ? "Azure DevOps" : "Jira/DevOps";

    const ambientSyncStatus = useAmbientSyncStatus(
        syncButton.show ? userStoryId : undefined);

    const buildFileName = async (extension: "docx" | "pdf") => {
        const parts: string[] = [];

        if (projectId) parts.push(`${projectId}_`);
        if (featureId) parts.push(`${featureId}_`);
        const timestamp = getTimestamp();

        let userStoryData: any = null;
        if (sectionId && userStoryId) {
            userStoryData = await getUserStoryData(userStoryId, sectionId);
        }
        if (userStoryData) {
            if (userStoryData.story_number) {
                parts.push(`${userStoryData.story_number}_`);
            }

            if (userStoryData.workproduct_id) {
                parts.push(`${userStoryData.workproduct_id}_`);
            } else if (userStoryData.workproductId) {
                parts.push(`${userStoryData.workproductId}_`);
            }

            if (userStoryData.workproduct_title) {
                parts.push(`${userStoryData.workproduct_title}_`);
            } else if (userStoryData.title) {
                parts.push(`${userStoryData.title}_`);
            }
        } else {
            if (workProductId) {
                parts.push(`${workProductId}_`);
            }
            if (workProductTitle) {
                parts.push(`${workProductTitle}_`);
            }
        }
        // Absolute safety fallback
        if (parts.length === 0) {
            parts.push("WorkProduct");
        }

        return `${parts.join("_")}_${timestamp}.${extension}`;
    };

    if (!editor) {
        return null;
    }

    // Get current text color from editor
    const currentColor = editor.getAttributes("textStyle").color || "#000000";

    // Get current font and normalize it to match our MenuItem values
    const rawFont = editor.getAttributes("textStyle").fontFamily || "";
    console.log("[TiptapToolbar] Raw font from editor:", rawFont);

    // Normalize font family to match MenuItem values
    let currentFont = "default";
    if (rawFont) {
        // Remove extra quotes and normalize spacing
        const normalized = rawFont.replace(/['"]/g, "").toLowerCase().trim();

        if (normalized.includes("arial")) {
            currentFont = "Arial";
        } else if (normalized.includes("courier")) {
            currentFont = "'Courier New', monospace";
        } else if (normalized.includes("georgia") || normalized.includes("merriweather")) {
            currentFont = "Georgia, 'Merriweather', serif";
        } else if (normalized.includes("times")) {
            currentFont = "'Times New Roman', serif";
        } else if (normalized.includes("verdana") || normalized.includes("noto sans")) {
            currentFont = "Verdana, 'Noto Sans', sans-serif";
        } else if (normalized.includes("comic sans") || normalized.includes("comic neue")) {
            currentFont = "'Comic Sans MS', 'Comic Neue', cursive";
        } else if (normalized.includes("impact") || normalized.includes("oswald")) {
            currentFont = "Impact, 'Oswald', sans-serif";
        } else {
            // Use the raw value if it matches exactly
            currentFont = rawFont;
        }
    }

    console.log("[TiptapToolbar] Normalized current font:", currentFont);

    // Extract font size and convert to just the number (remove 'pt' or 'px' suffix)
    const fontSizeAttr = editor.getAttributes("textStyle").fontSize || "12pt";
    const currentFontSize = fontSizeAttr.replace(/[a-z]+$/i, "") || "12";

    // Handle zoom
    const handleZoom = (newZoom: number) => {
        setZoomLevel(newZoom);
        // Apply zoom to editor content container
        const editorContent = document.querySelector(".tiptap-editor-content");
        if (editorContent) {
            (editorContent as HTMLElement).style.zoom = `${newZoom}%`;
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                gap: 0.5,
                padding: "4px 8px",
                borderBottom: "1px solid #e0e0e0",
                backgroundColor: "#fafafa",
                flexWrap: "wrap",
                alignItems: "center",
                borderRadius: "8px 8px 0 0",
                minHeight: "36px"
            }}
        >
            {/* Export controls - Word, PDF, Jira */}
            <ButtonGroup size="small" sx={{ height: "26px", ml: 0.5 }}>
                <ToolbarButton
                    onClick={async () => {
                        try {
                            console.log("[Export] Word export - allSections:", allSections);
                            // Prepare sections data for export
                            const sectionsData =
                                allSections && allSections.length > 0
                                    ? allSections
                                          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                                          .map(section => ({
                                              section_title: section.title,
                                              content: section.markdown
                                          }))
                                    : undefined;

                            console.log("[Export] Word sectionsData prepared:", sectionsData?.length || 0, "sections");
                            await exportToWord(editor, await buildFileName("docx"), sectionsData, workProductTitle);
                        } catch (error) {
                            console.error("Failed to export to Word:", error);
                            alert("Failed to export to Word. Please try again.");
                        }
                    }}
                    icon={<FontAwesomeIcon icon={faFileWord} size="sm" />}
                    tooltip="Export to Word (.docx)"
                />

                <ToolbarButton
                    onClick={async () => {
                        if (isExportingPdf) return;
                        setIsExportingPdf(true);
                        try {
                            console.log("[Export] PDF export - allSections:", allSections);
                            // Prepare sections data for export
                            const sectionsData =
                                allSections && allSections.length > 0
                                    ? allSections
                                          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                                          .map(section => ({
                                              section_title: section.title,
                                              content: section.markdown
                                          }))
                                    : undefined;

                            console.log("[Export] PDF sectionsData prepared:", sectionsData?.length || 0, "sections");

                            // Get authentication token
                            const token = await getToken();
                            if (!token) {
                                throw new Error("Unable to get authentication token");
                            }

                            const pdfFilename = await buildFileName("pdf");

                            // Use backend WeasyPrint conversion as the canonical export path.
                            // Do not silently fall back to client-side export, because it masks
                            // backend conversion failures and makes pagination debugging ambiguous.
                            await exportToPDFViaWord(editor, pdfFilename, sectionsData, workProductTitle, token);
                        } catch (error) {
                            console.error("Failed to export to PDF:", error);
                            const errorMessage = error instanceof Error
                                ? `Failed to export to PDF via backend: ${error.message}`
                                : "Failed to export to PDF via backend. Please try again in a moment.";
                            alert(errorMessage);
                        } finally {
                            setIsExportingPdf(false);
                        }
                    }}
                    disabled={isExportingPdf}
                    icon={<FontAwesomeIcon icon={faFilePdf} size="sm" />}
                    tooltip={isExportingPdf ? "Generating PDF..." : "Export to PDF"}
                />
                {syncButton.show && (
                    <span style={{ position: "relative", display: "inline-flex" }}>
                        <ToolbarButton
                            onClick={() => setSyncPanelOpen(true)}
                            icon={
                                syncButton.vendor === "JIRA" ? (
                                    <JiraIcon width={16} height={16} />
                                ) : syncButton.vendor === "AZURE_DEVOPS" ? (
                                    <AzureDevOpsIcon width={16} height={16} />
                                ) : (
                                    <FontAwesomeIcon icon={faArrowsRotate} size="sm" />
                                )
                            }
                            tooltip={
                                ambientSyncStatus === "CONFLICT"
                                    ? `Sync conflict — resolve in ${vendorLabel} sync`
                                    : ambientSyncStatus === "REMOTE_CHANGED"
                                        ? `Updated in ${vendorLabel} — review changes`
                                        : syncButton.tooltip
                            }
                        />
                        {(ambientSyncStatus === "REMOTE_CHANGED" || ambientSyncStatus === "CONFLICT") && (
                            <span
                                data-testid="sync-ambient-badge"
                                style={{
                                    position: "absolute", top: 1, right: 1,
                                    width: 7, height: 7, borderRadius: "50%",
                                    backgroundColor: ambientSyncStatus === "CONFLICT" ? "#e11d48" : "#f59e0b",
                                    pointerEvents: "none",
                                }}
                            />
                        )}
                    </span>
                )}
                {needsRebuild && onRebuild && (
                    <ToolbarButton onClick={onRebuild} icon={<RefreshIcon size={20} />} tooltip="Rebuild - content may be outdated" />
                )}
            </ButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: "20px", alignSelf: "center" }} />

            {/* Undo/Redo */}
            <ButtonGroup size="small" sx={{ height: "26px" }}>
                <ToolbarButton
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                    icon={<FontAwesomeIcon icon={faRotateLeft} size="sm" />}
                    tooltip="Undo (Ctrl+Z)"
                />
                <ToolbarButton
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                    icon={<FontAwesomeIcon icon={faRotateRight} size="sm" />}
                    tooltip="Redo (Ctrl+Y)"
                />
            </ButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: "20px", alignSelf: "center" }} />

            {/* Font Family Selector */}
            <Box
                component="select"
                value={currentFont}
                onChange={e => {
                    const selectedFont = (e.target as HTMLSelectElement).value;
                    if (selectedFont === "default") {
                        editor.chain().focus().unsetFontFamily().run();
                    } else {
                        editor.chain().focus().setFontFamily(selectedFont).run();
                    }
                }}
                onMouseDown={e => e.stopPropagation()}
                sx={{
                    minWidth: 120,
                    height: "26px",
                    fontSize: "12px",
                    border: "1px solid rgba(0, 0, 0, 0.23)",
                    borderRadius: "4px",
                    px: "8px",
                    backgroundColor: "#fff",
                    color: "rgba(0, 0, 0, 0.87)",
                    outline: "none",
                    "&:focus": {
                        borderColor: "#1976d2"
                    }
                }}
            >
                <option value="default" style={{ fontFamily: "inherit" }}>
                    Default
                </option>
                <option value="Arial" style={{ fontFamily: "Arial" }}>
                    Arial
                </option>
                <option value="'Courier New', monospace" style={{ fontFamily: "'Courier New', monospace" }}>
                    Courier New
                </option>
                <option value="Georgia, 'Merriweather', serif" style={{ fontFamily: "Georgia, 'Merriweather', serif" }}>
                    Georgia
                </option>
                <option value="'Times New Roman', serif" style={{ fontFamily: "'Times New Roman', serif" }}>
                    Times New Roman
                </option>
                <option value="Verdana, 'Noto Sans', sans-serif" style={{ fontFamily: "Verdana, 'Noto Sans', sans-serif" }}>
                    Verdana
                </option>
                <option value="'Comic Sans MS', 'Comic Neue', cursive" style={{ fontFamily: "'Comic Sans MS', 'Comic Neue', cursive" }}>
                    Comic Sans MS
                </option>
                <option value="Impact, 'Oswald', sans-serif" style={{ fontFamily: "Impact, 'Oswald', sans-serif" }}>
                    Impact
                </option>
            </Box>

            {/* Font Size Selector */}
            <Box
                component="select"
                value={currentFontSize}
                onChange={e => {
                    const selectedSize = (e.target as HTMLSelectElement).value;
                    if (selectedSize === "default") {
                        editor.chain().focus().unsetFontSize().run();
                    } else {
                        editor.chain().focus().setFontSize(selectedSize + "pt").run();
                    }
                }}
                onMouseDown={e => e.stopPropagation()}
                sx={{
                    minWidth: 60,
                    height: "26px",
                    fontSize: "12px",
                    border: "1px solid rgba(0, 0, 0, 0.23)",
                    borderRadius: "4px",
                    px: "8px",
                    backgroundColor: "#fff",
                    color: "rgba(0, 0, 0, 0.87)",
                    outline: "none",
                    "&:focus": {
                        borderColor: "#1976d2"
                    }
                }}
            >
                <option value="default">Default</option>
                <option value="8">8</option>
                <option value="9">9</option>
                <option value="10">10</option>
                <option value="11">11</option>
                <option value="12">12</option>
                <option value="14">14</option>
                <option value="16">16</option>
                <option value="18">18</option>
                <option value="20">20</option>
                <option value="22">22</option>
                <option value="24">24</option>
                <option value="26">26</option>
                <option value="28">28</option>
                <option value="36">36</option>
                <option value="48">48</option>
                <option value="72">72</option>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: "20px", alignSelf: "center" }} />

            {/* Text color */}
            <Tooltip title="Text Color" arrow placement="bottom">
                <IconButton
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        setColorAnchor(e.currentTarget);
                    }}
                    size="small"
                    sx={{
                        width: "24px",
                        height: "24px",
                        padding: "2px",
                        borderRadius: "3px",
                        "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.08)"
                        }
                    }}
                >
                    <Box sx={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <FontAwesomeIcon icon={faPalette} size="sm" />
                        <Box
                            sx={{
                                position: "absolute",
                                bottom: -1,
                                left: 2,
                                right: 2,
                                height: "2px",
                                backgroundColor: currentColor,
                                borderRadius: "1px"
                            }}
                        />
                    </Box>
                </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: "20px", alignSelf: "center" }} />

            {/* Text formatting */}
            <ButtonGroup size="small" sx={{ height: "26px" }}>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    active={editor.isActive("bold")}
                    icon={<FontAwesomeIcon icon={faBold} size="sm" />}
                    tooltip="Bold (Ctrl+B)"
                />
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    active={editor.isActive("italic")}
                    icon={<FontAwesomeIcon icon={faItalic} size="sm" />}
                    tooltip="Italic (Ctrl+I)"
                />
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    active={editor.isActive("underline")}
                    icon={<FontAwesomeIcon icon={faUnderline} size="sm" />}
                    tooltip="Underline (Ctrl+U)"
                />
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    active={editor.isActive("strike")}
                    icon={<FontAwesomeIcon icon={faStrikethrough} size="sm" />}
                    tooltip="Strikethrough"
                />
            </ButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: "20px", alignSelf: "center" }} />

            {/* Code - Inline and Block together */}
            <ButtonGroup size="small" sx={{ height: "26px" }}>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleCode().run()}
                    active={editor.isActive("code")}
                    icon={<FontAwesomeIcon icon={faCode} size="sm" />}
                    tooltip="Inline Code (Ctrl+E)"
                />
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                    active={editor.isActive("codeBlock")}
                    icon={<FontAwesomeIcon icon={faBracketsCurly} size="sm" />}
                    tooltip="Code Block (Ctrl+Alt+C)"
                />
            </ButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: "20px", alignSelf: "center" }} />

            {/* Text alignment */}
            <ButtonGroup size="small" sx={{ height: "26px" }}>
                <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign("left").run()}
                    active={editor.isActive({ textAlign: "left" })}
                    icon={<FontAwesomeIcon icon={faAlignLeft} size="sm" />}
                    tooltip="Align Left"
                />
                <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign("center").run()}
                    active={editor.isActive({ textAlign: "center" })}
                    icon={<FontAwesomeIcon icon={faAlignCenter} size="sm" />}
                    tooltip="Align Center"
                />
                <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign("right").run()}
                    active={editor.isActive({ textAlign: "right" })}
                    icon={<FontAwesomeIcon icon={faAlignRight} size="sm" />}
                    tooltip="Align Right"
                />
                <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign("justify").run()}
                    active={editor.isActive({ textAlign: "justify" })}
                    icon={<FontAwesomeIcon icon={faAlignJustify} size="sm" />}
                    tooltip="Justify"
                />
            </ButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: "20px", alignSelf: "center" }} />

            {/* Lists and blocks */}
            <ButtonGroup size="small" sx={{ height: "26px" }}>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    active={editor.isActive("bulletList")}
                    icon={<FontAwesomeIcon icon={faListUl} size="sm" />}
                    tooltip="Bullet List (Ctrl+Shift+8)"
                />
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    active={editor.isActive("orderedList")}
                    icon={<FontAwesomeIcon icon={faListOl} size="sm" />}
                    tooltip="Numbered List (Ctrl+Shift+7)"
                />
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    active={editor.isActive("blockquote")}
                    icon={<FontAwesomeIcon icon={faQuoteRight} size="sm" />}
                    tooltip="Blockquote (Ctrl+Shift+B)"
                />
            </ButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: "20px", alignSelf: "center" }} />

            {/* Horizontal Rule */}
            <ToolbarButton
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
                icon={<FontAwesomeIcon icon={faMinus} size="sm" />}
                tooltip="Horizontal Rule"
            />

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: "20px", alignSelf: "center" }} />

            {/* Table operations */}
            <ButtonGroup size="small" sx={{ height: "26px" }}>
                <Tooltip title="Insert Table" arrow placement="bottom">
                    <IconButton
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            setTableAnchor(e.currentTarget);
                        }}
                        size="small"
                        sx={{
                            width: "24px",
                            height: "24px",
                            padding: "2px",
                            borderRadius: "3px",
                            "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.08)"
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faTable} size="sm" />
                    </IconButton>
                </Tooltip>
                <ToolbarButton
                    onClick={() => editor.chain().focus().addRowBefore().run()}
                    disabled={!editor.can().addRowBefore()}
                    icon={<FontAwesomeIcon icon={faArrowUpFromBracket} size="sm" />}
                    tooltip="Add Row Above"
                />
                <ToolbarButton
                    onClick={() => editor.chain().focus().addRowAfter().run()}
                    disabled={!editor.can().addRowAfter()}
                    icon={<FontAwesomeIcon icon={faArrowDownToBracket} size="sm" />}
                    tooltip="Add Row Below"
                />
                <ToolbarButton
                    onClick={() => editor.chain().focus().deleteRow().run()}
                    disabled={!editor.can().deleteRow()}
                    icon={<FontAwesomeIcon icon={faTableRows} size="sm" style={{ color: "#d32f2f" }} />}
                    tooltip="Delete Row"
                />
                <ToolbarButton
                    onClick={() => editor.chain().focus().deleteColumn().run()}
                    disabled={!editor.can().deleteColumn()}
                    icon={<FontAwesomeIcon icon={faTableColumns} size="sm" style={{ color: "#d32f2f" }} />}
                    tooltip="Delete Column"
                />
                <ToolbarButton
                    onClick={() => editor.chain().focus().deleteTable().run()}
                    disabled={!editor.can().deleteTable()}
                    icon={<FontAwesomeIcon icon={faTrash} size="sm" style={{ color: "#d32f2f" }} />}
                    tooltip="Delete Table"
                />
                <ToolbarButton
                    onClick={() => editor.chain().focus().setCellAttribute("verticalAlign", "middle").run()}
                    disabled={!editor.can().setCellAttribute("verticalAlign", "middle")}
                    icon={<FontAwesomeIcon icon={faArrowsUpDown} size="sm" />}
                    tooltip="Align Cell Middle"
                />
            </ButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: "20px", alignSelf: "center" }} />

            {/* Zoom controls */}
            <ButtonGroup size="small" sx={{ height: "26px" }}>
                <ToolbarButton
                    onClick={() => handleZoom(Math.max(25, zoomLevel - 10))}
                    icon={<FontAwesomeIcon icon={faMagnifyingGlassMinus} size="sm" />}
                    tooltip="Zoom Out"
                    disabled={zoomLevel <= 25}
                />
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        px: 1,
                        fontSize: "11px",
                        color: "rgba(0, 0, 0, 0.87)",
                        minWidth: "45px",
                        justifyContent: "center"
                    }}
                >
                    {zoomLevel}%
                </Box>
                <ToolbarButton
                    onClick={() => handleZoom(Math.min(200, zoomLevel + 10))}
                    icon={<FontAwesomeIcon icon={faMagnifyingGlassPlus} size="sm" />}
                    tooltip="Zoom In"
                    disabled={zoomLevel >= 200}
                />
                <ToolbarButton
                    onClick={() => handleZoom(100)}
                    icon={<FontAwesomeIcon icon={faArrowsRotate} size="sm" />}
                    tooltip="Reset Zoom (100%)"
                    disabled={zoomLevel === 100}
                />
            </ButtonGroup>

            {/* Jira/Azure DevOps Sync Panel */}
            {userStoryId && (
                <SyncPanel
                    open={syncPanelOpen}
                    onClose={() => { setSyncPanelOpen(false); clearAmbientSyncCache(); }}
                    scope={{ type: "story", id: userStoryId }}
                    currentStoryId={userStoryId}
                    onNavigateToIntegrations={onNavigateToIntegrations}
                />
            )}

            {/* Table Size Selector Popover - Word-like Grid Selector */}
            <Popover
                open={Boolean(tableAnchor)}
                anchorEl={tableAnchor}
                onClose={() => {
                    setTableAnchor(null);
                    setHoveredCell(null);
                }}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left"
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left"
                }}
            >
                <Paper sx={{ p: 2 }}>
                    {/* Display current selection */}
                    <Box
                        sx={{
                            mb: 1,
                            textAlign: "center",
                            fontSize: "12px",
                            fontWeight: 500,
                            minHeight: "18px",
                            color: "rgba(0, 0, 0, 0.87)"
                        }}
                    >
                        {hoveredCell ? `${hoveredCell.row} x ${hoveredCell.col} Table` : "Select table size"}
                    </Box>

                    {/* Grid of cells - 7 columns x 6 rows max */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(7, 20px)",
                            gridTemplateRows: "repeat(6, 20px)",
                            gap: "2px",
                            padding: "4px"
                        }}
                        onMouseLeave={() => setHoveredCell(null)}
                    >
                        {Array.from({ length: 6 }, (_, rowIndex) =>
                            Array.from({ length: 7 }, (_, colIndex) => {
                                const row = rowIndex + 1;
                                const col = colIndex + 1;
                                const isHighlighted = hoveredCell && row <= hoveredCell.row && col <= hoveredCell.col;

                                return (
                                    <Box
                                        key={`${row}-${col}`}
                                        onMouseEnter={() => setHoveredCell({ row, col })}
                                        onClick={() => {
                                            if (hoveredCell) {
                                                editor
                                                    .chain()
                                                    .focus()
                                                    .insertTable({
                                                        rows: hoveredCell.row,
                                                        cols: hoveredCell.col,
                                                        withHeaderRow: true
                                                    })
                                                    .run();
                                                setTableAnchor(null);
                                                setHoveredCell(null);
                                            }
                                        }}
                                        sx={{
                                            width: "20px",
                                            height: "20px",
                                            border: "1px solid #d0d0d0",
                                            backgroundColor: isHighlighted ? "#0066cc" : "#ffffff",
                                            cursor: "pointer",
                                            transition: "background-color 0.05s ease",
                                            "&:hover": {
                                                backgroundColor: isHighlighted ? "#0052a3" : "#e6f2ff"
                                            }
                                        }}
                                    />
                                );
                            })
                        )}
                    </Box>
                </Paper>
            </Popover>

            {/* Text Color Picker */}
            <Popover
                open={Boolean(colorAnchor)}
                anchorEl={colorAnchor}
                onClose={() => setColorAnchor(null)}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left"
                }}
            >
                <Paper sx={{ p: 2, minWidth: 250 }}>
                    <Grid container spacing={1}>
                        {[
                            { color: "#000000", label: "Black" },
                            { color: "#FFFFFF", label: "White" },
                            { color: "#FF0000", label: "Red" },
                            { color: "#00FF00", label: "Green" },
                            { color: "#0000FF", label: "Blue" },
                            { color: "#FFFF00", label: "Yellow" },
                            { color: "#FF00FF", label: "Magenta" },
                            { color: "#00FFFF", label: "Cyan" },
                            { color: "#FFA500", label: "Orange" },
                            { color: "#800080", label: "Purple" },
                            { color: "#FFC0CB", label: "Pink" },
                            { color: "#808080", label: "Gray" }
                        ].map(({ color, label }) => (
                            <Grid item xs={3} key={color}>
                                <Tooltip title={label}>
                                    <Box
                                        onClick={() => {
                                            editor.chain().focus().setColor(color).run();
                                            setColorAnchor(null);
                                        }}
                                        sx={{
                                            width: "100%",
                                            height: 40,
                                            backgroundColor: color,
                                            border: currentColor === color ? "3px solid #0066cc" : "1px solid #ccc",
                                            cursor: "pointer",
                                            borderRadius: "4px",
                                            "&:hover": {
                                                opacity: 0.8,
                                                transform: "scale(1.05)"
                                            },
                                            transition: "all 0.2s"
                                        }}
                                    />
                                </Tooltip>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            </Popover>
        </Box>
    );
}
